import { GetPickupGuideOutput } from '@allocab/allocab-commons-shared-types';
import type { NoteTripInput, TripOutput } from '@allocab/allocab-passenger-shared-types';

import { PanelDetail } from './PanelDetail';
import { BookingConfirmed } from './step-trip/BookingConfirmed';
import { Cancelled } from './step-trip/Cancelled';
import { Done } from './step-trip/Done';
import { DriverApproaching } from './step-trip/DriverApproaching';
import { DriverInPlace } from './step-trip/DriverInPlace';
import { DriverPreviousDropOff } from './step-trip/DriverPreviousDropOff';
import { InvoiceDownloadButton } from './step-trip/InvoiceDownloadButton';
import { OnDuty } from './step-trip/OnDuty';
import { SearchingDriver } from './step-trip/SearchingDriver';
import { TripDriver } from './TripDriver';
import { TripRating } from './TripRating';
import { TripResume } from './TripResume';
import { constants } from '../../constants';
import { t } from '../../services/translation/translation';
import { assertNever } from '../../utils/common';
import { getStepForTrip } from '../../utils/trip';

interface Props {
  jwtToken?: string;
  trip: TripOutput;
  searchingDriverRef: React.RefObject<HTMLDivElement>;
  onCreateNote?: (_input: NoteTripInput) => void;
  isCreatingNote: boolean;
  dataGuide?: GetPickupGuideOutput;
}

export const TripDetail = ({ trip, searchingDriverRef, onCreateNote, isCreatingNote, dataGuide, jwtToken }: Props) => {
  const step = getStepForTrip(trip);
  const isInvoiceReady = trip.resume.invoiceStatusPassenger === 'INVOICED';
  const renderStep = () => {
    switch (step) {
      case 'BOOKING_CONFIRMED':
        return <BookingConfirmed trip={trip} />;
      case 'SEARCHING_DRIVER':
        return <SearchingDriver searchingDriverRef={searchingDriverRef} trip={trip} />;
      case 'DRIVER_PREVIOUS_DROP_OFF':
        return <DriverPreviousDropOff trip={trip} />;
      case 'DRIVER_APPROACHING':
        return <DriverApproaching trip={trip} />;
      case 'DRIVER_IN_PLACE':
        return <DriverInPlace trip={trip} />;
      case 'ONDUTY':
        return <OnDuty trip={trip} />;
      case 'CANCELLED':
        return <Cancelled trip={trip} />;
      case 'DONE':
        return <Done trip={trip} />;
      default:
        assertNever(step, '[TripDetail] Trip step unknown');
        return null;
    }
  };
  return (
    <div className="flex flex-col gap-y-2">
      {renderStep()}
      <hr />
      {step === 'DONE' ? (
        <>
          <TripRating
            trip={trip}
            onCreateNote={!constants.isHotels ? onCreateNote : undefined}
            isLoading={isCreatingNote}
          />
          <hr />
        </>
      ) : null}

      {trip?.driver ? (
        <>
          <TripDriver driver={trip.driver} step={step} />
          <hr />
        </>
      ) : null}

      <TripResume dataGuide={dataGuide} trip={trip} />

      <PanelDetail trip={trip} />

      {(isInvoiceReady && step === 'DONE') || (isInvoiceReady && step === 'CANCELLED') ? (
        <div className="flex items-center w-full min-h-200 shadow-md shadow-shadow p-2">
          <div>{t('share-route.trip-detail.download-invoice')}</div>
          <InvoiceDownloadButton jwtToken={jwtToken} tripKey={trip.key} />
        </div>
      ) : null}
    </div>
  );
};
